"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Phone, Dog } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuLink as NavigationMenuLinkPrimitive, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="25168" className="hidden md:flex gap-6">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.filter(item => item.label !== "New Page" && item.href !== "/NewPage").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-base font-bold text-foreground transition-colors hover:text-primary tracking-tight">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-full origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block rounded-md px-4 py-3 text-lg font-bold text-foreground hover:bg-accent hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-4 flex flex-col gap-3 px-4">
        <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md" asChild>
          <Link to="https://booking.moego.pet/ol/landing?name=BarkBathMobileGroomingLLC">Book Now</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out border-b bg-background text-foreground", isScrolled ? "bg-white/80 backdrop-blur-md border-primary/20 shadow-sm" : "bg-white border-transparent")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        
        <Button variant="outline" size="sm" className="sticky top-0 z-10 md:hidden rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold transition-all shadow-sm" asChild>
          <Link to="tel:(302) 787-9274" className="inline-flex items-center justify-center whitespace-nowrap text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border bg-background h-8 gap-1.5 px-3 has-[>svg]:px-2.5 sticky top-0 z-10 rounded-full border-primary text-primary font-bold transition-all shadow-sm inline-flex items-center justify-center whitespace-nowrap text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border bg-background h-8 gap-1.5 px-3 has-[>svg]:px-2.5 sticky top-0 z-10 rounded-full border-primary text-primary font-bold transition-all shadow-sm ![margin:0px_8px_0px_0px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 md:hidden hover:bg-primary hover:text-primary-foreground disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 md:hidden hover:bg-primary hover:text-primary-foreground">
            <Phone className="size-4" />
            302-PUP-WASH
          </Link>
        </Button>

        <div className="flex items-center gap-2">
          <Link to="#" className="flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md">
            <img src="https://barkbathmobile.com/wp-content/uploads/2026/04/bark-bath-logo-transparent.webp" alt="Bark Bath Mobile Grooming" className="w-auto object-contain !h-[52px]" />
          </Link>
        </div>

        

        
        <div className="hidden md:block">
          <MenuProvider menu_id="42" className="w-full">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        
        <div className="hidden items-center gap-4 md:flex">
          <Button className="rounded-full bg-primary px-8 font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105 active:scale-95" asChild>
            <Link to="https://booking.moego.pet/ol/landing?name=BarkBathMobileGroomingLLC">Book Now</Link>
          </Button>
          <Button variant="outline" className="rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold transition-all" asChild>
            <Link to="tel:(302) 787-9274">
              <Phone />
              302-PUP-WASH
            </Link>
          </Button>
        </div>

        
        <Button variant="ghost" size="icon" className="md:hidden text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      
      {isMobileMenuOpen && <div className="absolute left-0 top-20 z-40 w-full border-b border-primary/10 bg-white shadow-xl md:hidden animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="42" className="w-full">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}